<script>
import { MOLDURA_DO_THREAD } from '@modules/aawz-intelligence-v2/ui-registry/moldura-do-thread'
import { ROTULO_SERVICO_CITADO } from '@modules/aawz-intelligence-v2/constants/Text'
import { BLOCO_SERVICOS_CITADOS } from '@modules/aawz-intelligence-v2/constants/TextChat'
import { apiRascunhoEntrega } from '@modules/aawz-intelligence-v2/api/apiRascunhoEntrega'
import { downloadBase64 } from '@utils/Download'

// O backend já trunca; o componente fatia por segurança. Três é o teto do wireframe T-4:
// acima disso o bloco deixa de ser "próximo passo" e vira catálogo.
const MAXIMO_ITENS = 3

const ROTULO_COM_CICLO = 'obrigatorio_no_ciclo'

// Cor por rótulo — a cor carrega significado, não decoração. Todas são tokens da
// paleta (`parameters/state.js`), nunca hex inline.
const COR_POR_ROTULO = {
  obrigatorio_no_ciclo: 'warning-new',
  opcional_no_ciclo: 'secondary',
  o_que_vem_depois: 'info',
}
const COR_PADRAO = 'secondary'

const ERRO_DOWNLOAD = 'Não foi possível gerar o rascunho agora. Tente de novo.'

// A geração personalizada chama o modelo e leva dezenas de segundos. Silêncio
// nesse intervalo lê-se como travado — e o gestor clica de novo.
const AVISO_ESCREVENDO = 'Escrevendo o rascunho a partir da sua conversa. Leva alguns instantes.'

export default {
  name: 'IntelligenceV2ServicosCitados',
  props: {
    itens: {
      type: Array,
      default: function() { return [] },
    },
    // Sem ela o backend devolve o rascunho ESTRUTURAL (o da spec-001) em vez do
    // personalizado — degrada, não quebra.
    idConversa: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      MOLDURA_DO_THREAD,
      // Nasce ABERTO: o bloco chega junto da resposta e o gestor precisa ver o
      // que a IA citou. Colapsar é para depois de ler — mesmo gesto dos outros
      // blocos do chat (`raciocinio-linha`, `manager-questions`).
      aberto: true,
      // Chave do cartão cujo arquivo está sendo gerado, e a do último que falhou
      // — com o motivo que o backend deu. É a CHAVE e não o código porque o
      // backend pode citar o mesmo serviço duas vezes: chaveado por código, um
      // clique acenderia o spinner nos dois cartões.
      baixandoChave: '',
      erroChave: '',
      erroMensagem: '',
    }
  },
  computed: {
    classeSeta() {
      return this.aberto ? 'ia-block-seta--aberta' : ''
    },
    // Colapsado, a fita diz quantos serviços estão escondidos ali.
    resumo() {
      return String(this.itens.length)
    },
    titulo() {
      return BLOCO_SERVICOS_CITADOS.titulo
    },
    // Itens já decorados: o template não chama função (cada re-render reexecutaria).
    itensDecorados() {
      const visiveis = (this.itens || []).slice(0, MAXIMO_ITENS)
      const baixandoChave = this.baixandoChave
      const erroChave = this.erroChave
      const erroMensagem = this.erroMensagem
      return visiveis.map(function(item, indice) {
        // Índice no sufixo: o backend pode repetir o mesmo tipo e `:key` duplicado
        // gera warning do Vue e erro de atualização. A lista é curta e não reordena.
        const chave = (item.id_solicitacao_tipo || item.codigo || 'item') + '-' + indice
        return {
          ...item,
          ordem: indice + 1,
          chave: chave,
          rotuloTexto: montarRotulo(item),
          rotuloCor: COR_POR_ROTULO[item.rotulo] || COR_PADRAO,
          rotuloAcao: 'Abrir a página do serviço ' + (item.nome_canonico || ''),
          // Mensagem persistida antes desta feature não tem o campo — `undefined`
          // é falsy e o botão simplesmente não aparece. Nada quebra ao reabrir
          // uma conversa antiga.
          mostraRascunho: item.rascunho_disponivel === true,
          rascunhoAcao: 'Baixar o rascunho do desenho de entrega de '
            + (item.nome_canonico || ''),
          baixando: chave === baixandoChave,
          aviso: chave === baixandoChave ? AVISO_ESCREVENDO : '',
          // Um download de cada vez — e o botão do irmão PRECISA dizer isso. Sem
          // `bloqueado` ele fica com aparência normal, o clique não faz nada e o
          // gestor não tem como saber por quê.
          bloqueado: !!baixandoChave && chave !== baixandoChave,
          erro: chave === erroChave ? (erroMensagem || ERRO_DOWNLOAD) : '',
        }
      })
    },
    temItens() {
      return this.itensDecorados.length > 0
    },
  },
  watch: {
    // Turno novo apaga o erro do anterior: sem isto, um cartão do mesmo serviço
    // reaparece já pintado de vermelho por uma falha que era de outra resposta.
    itens() {
      this.erroChave = ''
      this.erroMensagem = ''
    },
  },
  methods: {
    alternar() {
      this.aberto = !this.aberto
    },
    async baixarRascunho(item) {
      if (this.baixandoChave) return
      this.baixandoChave = item.chave
      this.erroChave = ''
      this.erroMensagem = ''
      try {
        const r = await apiRascunhoEntrega.fetch(item.codigo, this.idConversa)
        if (r && r.arquivo) {
          downloadBase64(r.arquivo.base64, r.arquivo.name, r.arquivo.extension)
          return
        }
        // `con.get` NÃO lança em falha de rede ou HTTP — devolve o envelope com o
        // motivo. Por isso a mensagem do backend chega por aqui, e não pelo catch.
        // Vale para os dois tipos de recusa, que pedem ações diferentes do gestor:
        // o 422 diz o que ainda FALTA responder na conversa (transforma beco sem
        // saída em próximo passo); os demais dizem por que não deu.
        this.registrarFalha(item, r && r.message)
      } catch (e) {
        // O que de fato lança aqui é o `downloadBase64` (base64 corrompido), não
        // a requisição.
        console.warn('[ia-crm] rascunho de entrega falhou', e)
        this.registrarFalha(item, '')
      } finally {
        this.baixandoChave = ''
      }
    },
    registrarFalha(item, mensagem) {
      this.erroChave = item.chave
      this.erroMensagem = mensagem || ERRO_DOWNLOAD
    },
    abrirServico(item) {
      // Emite; NÃO navega. O módulo de IA não conhece a Jornada — quem chama
      // `abrirLandingServico` é a casca em `solicitacoes`.
      this.$emit('open-service', {
        idTipo: item.id_solicitacao_tipo,
        codigo: item.codigo,
        nome: item.nome_canonico,
      })
    },
  },
}

/**
 * Texto do rótulo exibido no chip.
 *
 * `nivel_ciclo_referencia === 0` é VÁLIDO (C0, "Em constituição"): a checagem é por
 * `null`/`undefined`, nunca por truthiness — `if (item.nivel_ciclo_referencia)`
 * esconderia justamente o escritório que mais precisa do consultor.
 *
 * @param {Object} item Item `ServicoCitado`.
 * @returns {String} Texto do rótulo, ou string vazia para rótulo desconhecido.
 */
function montarRotulo(item) {
  const base = ROTULO_SERVICO_CITADO[item.rotulo] || ''
  const nivel = item.nivel_ciclo_referencia
  if (item.rotulo !== ROTULO_COM_CICLO) return base
  if (nivel === null || nivel === undefined) return base
  return 'Obrigatório em C' + nivel
}
</script>

<template>
  <div v-if="temItens" class="servicos-citados ia-block-card" v-bind="MOLDURA_DO_THREAD">
    <!-- `button`, não `div`: sem foco e sem Enter/Espaço o bloco colapsado fica
         inalcançável por teclado. Mesmo elemento da `raciocinio-linha`. -->
    <button
      type="button"
      class="ia-block-card__cabecalho ia-block-card__cabecalho--fita ia-block-card__cabecalho--clicavel"
      :aria-expanded="String(aberto)"
      @click="alternar"
    >
      <icon-svg
        name="book"
        :size="14"
        color="var(--primary)"
        class="ia-block-card__icone"
      />
      <span class="ia-block-card__titulo">{{ titulo }}</span>
      <span class="ia-block-card__meta">{{ resumo }}</span>
      <icon-svg
        name="arrow-down-new-ux"
        :size="14"
        color="var(--neutral-700)"
        class="ia-block-seta"
        :class="classeSeta"
      />
    </button>

    <div
      v-for="item in itensDecorados"
      v-show="aberto"
      :key="item.chave"
      class="servico-citado-item"
    >
      <span class="servico-citado-ordem ia-texto-selo">{{ item.ordem }}</span>

      <div class="servico-citado-corpo">
        <div class="servico-citado-topo">
          <span class="servico-citado-nome ia-texto-auxiliar">{{ item.nome_canonico }}</span>
          <BaseStatus
            soft
            preserve-case
            :content="item.rotuloTexto"
            :color="item.rotuloCor"
          />
        </div>
        <div class="servico-citado-motivo ia-texto-auxiliar">{{ item.motivo }}</div>
        <!-- Estado ESCREVENDO (#2055): o rascunho personalizado leva dezenas de
             segundos, e sem este aviso o cartão ficava mudo e o gestor reclicava.
             `<output>` e não `<div>` porque é região viva — o leitor de tela
             anuncia a mudança sem precisar de `aria-live` na mão. -->
        <div v-if="item.aviso" class="servico-citado-motivo ia-texto-auxiliar">
          <output>{{ item.aviso }}</output>
        </div>
        <div v-if="item.erro" class="servico-citado-erro ia-texto-auxiliar" role="alert">
          {{ item.erro }}
        </div>
      </div>

      <!-- Duas ações no mesmo alvo discreto de 28px (`.servico-citado-acao`), não
           em `BaseButton underlined`: o retângulo roxo do BaseButton ao lado de
           CADA linha competia com o conteúdo — é a razão de a ação ter virado
           este botão, e vale igual para o download. O `min-width` que o teste
           do #2054 protegia deixa de existir por construção: `<button>` puro não
           reserva largura, então o `motivo` não perde espaço. -->
      <div class="servico-citado-acoes">
        <button
          v-if="item.mostraRascunho"
          type="button"
          class="servico-citado-acao"
          data-test="baixar-rascunho"
          :disabled="item.baixando || item.bloqueado"
          :aria-label="item.rascunhoAcao"
          @click="baixarRascunho(item)"
        >
          <icon-svg
            name="document-download"
            :size="14"
            color="var(--neutral-700)"
            :class="item.baixando ? 'ia-shimmer-text' : ''"
          />
        </button>

        <button
          type="button"
          class="servico-citado-acao"
          data-test="abrir-servico"
          :aria-label="item.rotuloAcao"
          @click="abrirServico(item)"
        >
          <icon-svg name="go-out" :size="14" color="var(--neutral-700)" />
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
// Respiro contra o bloco anterior. Mora AQUI, e não numa utilitária no
// consumidor, porque o card é montado por mais de uma superfície e a distância
// é do bloco, não de quem o coloca: sem isto ele encostava no texto da resposta
// (medido: 0px de folga contra o `assistant-blocks`).
.servicos-citados
  margin-top: 12px

// Reset do `button` do cabeçalho: sem isto ele traria borda, fundo e fonte
// nativos e a fita destoaria dos outros blocos.
.ia-block-card__cabecalho
  width: 100%
  text-align: left
  font: inherit
  border: 0

// O reset acima ganha da partial por especificidade (o `scoped` soma um seletor
// de atributo), e levava junto a linha inferior da fita. Redeclarada aqui, com o
// MESMO token de `_ia-block-card.sass`.
.ia-block-card__cabecalho--fita
  border-bottom: 1px solid var(--border-hairline)

// Card, faixa e titulo vem de `.ia-block-card*` (partial de modulo) — o bloco
// passou a ter a mesma anatomia da deliberacao e dos proximos passos, que e o
// que o resto da resposta usa. Antes era uma lista solta com filete no topo.
.servico-citado-item
  display: flex
  align-items: flex-start
  gap: 10px
  padding: 10px 14px

.servico-citado-item + .servico-citado-item
  border-top: 1px solid var(--border-hairline)

// Disco numerado, igual ao dos proximos passos.
.servico-citado-ordem
  color: var(--neutral-700)
  display: inline-flex
  align-items: center
  justify-content: center
  flex-shrink: 0
  width: 20px
  height: 20px
  margin-top: 1px
  border-radius: 50%
  background: var(--neutral-100)

.servico-citado-corpo
  display: flex
  flex-direction: column
  gap: 3px
  flex: 1 1 auto
  min-width: 0

// As duas ações lado a lado, encostadas no topo da linha.
.servico-citado-acoes
  display: flex
  align-items: center
  gap: 2px
  flex-shrink: 0

.servico-citado-topo
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 8px

// Acao discreta: o `BaseButton underlined` so com icone desenhava um retangulo
// roxo do tamanho de um botao de formulario ao lado de cada linha, competindo
// com o conteudo. Aqui e um alvo de 28px que so acende no hover.
.servico-citado-acao
  display: inline-flex
  align-items: center
  justify-content: center
  flex-shrink: 0
  width: 28px
  height: 28px
  padding: 0
  border: 0
  border-radius: var(--radius-sm)
  background: none
  cursor: pointer
  transition: background-color .15s ease

  &:hover
    background: var(--neutral-100)

  // Um download de cada vez: o irmão fica bloqueado enquanto um gera. Sem o
  // estado visual ele tem aparência normal, o clique não faz nada e o gestor
  // não descobre por quê (invariante do #2054, que o `disabled` do BaseButton
  // dava de graça).
  &:disabled
    cursor: default
    opacity: .45

  &:disabled:hover
    background: none

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.servico-citado-erro
  color: var(--danger-text)

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.servico-citado-nome
  color: var(--neutral-900)

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.servico-citado-motivo
  color: var(--neutral-700)

</style>
